import { Point, Polygon, Layer, IconType, GlobalContext, Line } from "../../../context/context";
import { processOSMBusData } from '../processors';
import { OpenStreetMapService, OSMConfig } from '../../../services/osm/OpenStreetMapService';
import { logEvent } from '../../../services/analytics';

export const OSM_ICON_MAP: { [key: string]: IconType } = {
    'amenity=school': 'school',
    'amenity=hospital': 'hospital',
    'amenity=parking': 'parking',
    'amenity=restaurant': 'restaurant',
    'shop=supermarket': 'store',
    'shop=convenience': 'store',
    'public_transport=station': 'train',
    'highway=bus_stop': 'bus',
    'aeroway=aerodrome': 'airport'
};

// Initialize OSM service with configuration
const osmService = new OpenStreetMapService({
    baseUrl: 'https://overpass-api.de',
    rateLimit: {
        requestsPerMinute: 30,
        requestsPerHour: 500,
    },
    cache: {
        ttl: 3600000, // 1 hour
        maxSize: 50,
    },
    timeout: 30000,
});

interface OSMLocationsImportProps {
    selectedTypes: string[];
    polygon: Polygon;
    global: GlobalContext;
    setOsmLocationsLoading: (loading: boolean) => void;
    setIsOsmLocationsModalOpen: (open: boolean) => void;
}

interface OSMBusDataImportProps {
    polygon: Polygon;
    global: GlobalContext;
    setOsmImportProgress: (progress: number | null) => void;
}

export async function handleOsmLocationsImport({
    selectedTypes,
    polygon,
    global,
    setOsmLocationsLoading,
    setIsOsmLocationsModalOpen
}: OSMLocationsImportProps) {
    logEvent({
        category: 'File Import',
        action: 'OSMImportInitiated',
        label: polygon.name || 'Unnamed Polygon',
        polygon_id: polygon.id,
        selected_types: selectedTypes,
        timestamp: Date.now()
    });
    setOsmLocationsLoading(true);
    try {
        // Build Overpass QL for selected POI types only
        const polyCoords = polygon.coordinates[0].map(([lat, lon]: [number, number]) => `${lat} ${lon}`).join(' ');
        const orBlocks = selectedTypes.map(type => {
            const [k, v] = type.split('=');
            return `node["${k}"="${v}"](poly:"${polyCoords}");`;
        }).join('\n');
        const query = `[out:json][timeout:60];(${orBlocks});out body;`;

        // Fetch from Overpass API
        const response = await fetch('https://overpass-api.de/api/interpreter', {
            method: 'POST',
            body: query,
            headers: { 'Content-Type': 'text/plain' },
        });
        const data = await response.json();

        // Parse to Points
        const points: Point[] = (data.elements || [])
            .filter((el: any) => el.type === 'node')
            .map((node: any) => {
                // Find which type this node matches
                let icon = 'default';
                let name = node.tags?.name || '';
                let typeKey = '';
                for (const t of selectedTypes) {
                    const [k, v] = t.split('=');
                    if (node.tags && node.tags[k] === v) {
                        icon = OSM_ICON_MAP[t] || 'default';
                        typeKey = t;
                        break;
                    }
                }
                return {
                    id: `osm-poi-${node.id}`,
                    type: 'point',
                    name: name || typeKey.split('=')[1] || 'POI',
                    description: node.tags?.description || '',
                    colour: '#1976d2',
                    icon: icon as IconType,
                    size: 24,
                    isLocked: false,
                    coordinates: [node.lat, node.lon],
                };
            });

        // Add as new layer
        if (points.length) {
            const newLayerId = global.mapLayers.length;
            const newLayer = {
                layerId: newLayerId,
                layerName: `OSM Locations`,
                hidden: false,
                shapes: points,
            };
            global.setLayers((prev: Layer[]) => [...prev, newLayer]);
            global.setActiveLayerId(newLayerId);
        }
    } catch (error) {
        console.error('Error importing OSM locations:', error);
    } finally {
        setOsmLocationsLoading(false);
        setIsOsmLocationsModalOpen(false);
    }
}

export async function handleOsmBusDataImport({
    polygon,
    global,
    setOsmImportProgress
}: OSMBusDataImportProps) {
    logEvent({
        category: 'File Import',
        action: 'OSMImportInitiated',
        label: polygon.name || 'Unnamed Polygon',
        polygon_id: polygon.id,
        timestamp: Date.now()
    });
    try {
        setOsmImportProgress(0);
        
        // Use the OpenStreetMapService to get transit data (bus stops only for now)
        const transitData = await osmService.getTransitData(polygon);
        setOsmImportProgress(50);
        
        // Convert bus stops to points
        const points: Point[] = transitData.stops.bus.map(stop => ({
            id: `osm-busstop-${stop.id}`,
            type: 'point',
            name: stop.name,
            description: `Bus stop with ${stop.routes.length} routes`,
            colour: '#1976d2',
            icon: 'bus',
            size: 24,
            isLocked: false,
            hidden: false,
            coordinates: stop.coordinates,
        }));

        // Create a simple route visualization by connecting nearby bus stops
        const lines: Line[] = [];
        const busStops = transitData.stops.bus;
        
        // Create simple route lines by connecting bus stops that are close together
        for (let i = 0; i < busStops.length - 1; i++) {
            const stop1 = busStops[i];
            const stop2 = busStops[i + 1];
            
            // Calculate distance between stops
            const lat1 = stop1.coordinates[0];
            const lon1 = stop1.coordinates[1];
            const lat2 = stop2.coordinates[0];
            const lon2 = stop2.coordinates[1];
            
            const distance = Math.sqrt(
                Math.pow(lat2 - lat1, 2) + Math.pow(lon2 - lon1, 2)
            );
            
            // Only connect stops that are reasonably close (within ~0.01 degrees)
            if (distance < 0.01) {
                lines.push({
                    id: `osm-route-${stop1.id}-${stop2.id}`,
                    type: 'line',
                    name: `Route ${stop1.name} to ${stop2.name}`,
                    description: 'Bus route segment',
                    colour: '#ff9800',
                    opacity: '0.8',
                    borderColour: '#e65100',
                    borderWidth: 2,
                    isLocked: false,
                    hidden: false,
                    coordinates: [stop1.coordinates, stop2.coordinates],
                });
            }
        }
        
        setOsmImportProgress(75);
        
        // Create new layer for OSM data
        const newLayer: Layer = {
            layerId: global.mapLayers.length,
            layerName: `OSM Transit Data - ${new Date().toLocaleString()}`,
            hidden: false,
            shapes: [...points, ...lines],
        };
        
        // Add to global state
        global.setLayers([...global.mapLayers, newLayer]);
        global.setActiveLayerId(newLayer.layerId);
        
        setOsmImportProgress(100);
        
        console.log(`✅ Imported ${points.length} bus stops and ${lines.length} route segments`);
        
    } catch (error) {
        console.error('Error importing OSM bus data:', error);
        throw error;
    }
}

// Export the service for use in other components
export { osmService };